import { useMemo, useState } from "react";
import api from "../services/api";
import AttendanceTracker from "../components/AttendanceTracker";

const AdminDashboard = ({
  session,
  users,
  attendance,
  applications = [],
  placements = [],
  onAddUser,
  onAssignTrainee,
  onSavePlacement,
  onDeletePlacement,
  onUpdateUser,
  onDeleteUser,
}) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "Trainer",
    assignedTrainer: "",
    profilePhoto: "",
  });
  const [formFile, setFormFile] = useState(null);
  const [assignPair, setAssignPair] = useState({ traineeId: "", trainerId: "" });
  const [placementForm, setPlacementForm] = useState({
    company: "",
    role: "",
    location: "",
    package: "",
    description: "",
  });
  const [editPlacementId, setEditPlacementId] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editUserFile, setEditUserFile] = useState(null);
  const [feedback, setFeedback] = useState("");

  const trainers = useMemo(() => users.filter((user) => user.role === "Trainer"), [users]);
  const trainees = useMemo(() => users.filter((user) => user.role === "Trainee"), [users]);
  const latestApplications = applications.slice(0, 5);

  const isAdmin = session && session.role === "Admin";

  if (!isAdmin) {
    return (
      <section className="dashboard dashboard-locked">
        <h2>Admin access required</h2>
        <p>Please login as an administrator to manage trainers, trainees, and placements.</p>
      </section>
    );
  }

  const handlePersonnelChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePersonnelSubmit = async (event) => {
    event.preventDefault();
    let photoUrl = form.profilePhoto || "";
    if (formFile) {
      const upload = await api.uploadProfilePhoto(formFile);
      photoUrl = upload.url;
    }
    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      password: form.password,
      role: form.role,
      assignedTrainer: form.assignedTrainer,
      profilePhoto: photoUrl,
    };
    await onAddUser(payload);
    setForm({ name: "", email: "", phone: "", password: "", role: "Trainer", assignedTrainer: "", profilePhoto: "" });
    setFormFile(null);
    setFeedback("User created.");
  };

  const handleAssign = async (event) => {
    event.preventDefault();
    if (!assignPair.traineeId || !assignPair.trainerId) return;
    await onAssignTrainee(Number(assignPair.traineeId), Number(assignPair.trainerId));
    setFeedback("Trainee assigned to trainer.");
    setAssignPair({ traineeId: "", trainerId: "" });
  };

  const handlePlacementChange = (event) => {
    const { name, value } = event.target;
    setPlacementForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePlacementSubmit = async (event) => {
    event.preventDefault();
    await onSavePlacement({ ...placementForm, id: editPlacementId });
    setFeedback(editPlacementId ? "Placement updated." : "Placement published and notifications sent.");
    setPlacementForm({ company: "", role: "", location: "", package: "", description: "" });
    setEditPlacementId(null);
  };

  const startEditPlacement = (placement) => {
    setEditPlacementId(placement.id);
    setPlacementForm({
      company: placement.company,
      role: placement.role,
      location: placement.location,
      package: placement.package,
      description: placement.description,
    });
  };

  const startEditUser = (user) => {
    setEditUser({
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      assignedTrainer: user.assignedTrainer || "",
      profilePhoto: user.profilePhoto || "",
      password: "",
    });
  };

  const handleUserUpdate = async (event) => {
    event.preventDefault();
    if (!editUser) return;
    let photoUrl = editUser.profilePhoto || "";
    if (editUserFile) {
      const upload = await api.uploadProfilePhoto(editUserFile, editUser.id);
      photoUrl = upload.url;
    }
    await onUpdateUser({ ...editUser, profilePhoto: photoUrl });
    setFeedback("User updated.");
    setEditUser(null);
    setEditUserFile(null);
  };

  return (
    <section className="dashboard admin-dashboard">
      <header>
        <p className="eyebrow">Admin Console</p>
        <h1>Oversee operations and placements.</h1>
      </header>

      <div className="dashboard-grid">
        <div className="card">
          <h3>Trainers</h3>
          <p className="stat">{trainers.length}</p>
          <ul>
            {trainers.map((trainer) => (
              <li key={trainer.id}>{trainer.name}</li>
            ))}
          </ul>
        </div>
        <div className="card">
          <h3>Trainees</h3>
          <p className="stat">{trainees.length}</p>
          <ul>
            {trainees.map((trainee) => (
              <li key={trainee.id}>
                {trainee.name}{" "}
                <span className="muted">
                  Trainer {trainee.assignedTrainer || "unassigned"}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="card">
          <h3>Attendance</h3>
          <p className="stat">{attendance.length}</p>
          <p className="muted">Full visibility on every team.</p>
        </div>
        <div className="card">
          <h3>Recent Placement Applicants</h3>
          {latestApplications.length === 0 && <p className="muted">No applications yet.</p>}
          <ul className="small">
            {latestApplications.map((app) => (
              <li key={app.id}>
                <strong>{app.trainee_name}</strong> → {app.company_name} ({app.job_role}){" "}
                <span className="muted">{app.status}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {feedback && <p className="success-callout">{feedback}</p>}

      <div className="dashboard-grid">
        <form className="form-card" onSubmit={handlePersonnelSubmit}>
          <h3>Create Trainer/Trainee</h3>
          <label>
            Role
            <select name="role" value={form.role} onChange={handlePersonnelChange}>
              <option value="Trainer">Trainer</option>
              <option value="Trainee">Trainee</option>
            </select>
          </label>
          <label>
            Full name
            <input name="name" value={form.name} onChange={handlePersonnelChange} required />
          </label>
          <label>
            Email
            <input name="email" value={form.email} onChange={handlePersonnelChange} required />
          </label>
          <label>
            Phone
            <input name="phone" value={form.phone} onChange={handlePersonnelChange} />
          </label>
          <label>
            Password
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={handlePersonnelChange}
              required
            />
          </label>
          <label>
            Profile photo URL (optional)
            <input
              name="profilePhoto"
              value={form.profilePhoto}
              onChange={handlePersonnelChange}
              placeholder="https://..."
            />
          </label>
          <label>
            Or upload photo
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setFormFile(e.target.files?.[0] || null)}
            />
          </label>
          {form.role === "Trainee" && (
            <label>
              Assign Trainer
              <select
                name="assignedTrainer"
                value={form.assignedTrainer}
                onChange={handlePersonnelChange}
              >
                <option value="">Select</option>
                {trainers.map((trainer) => (
                  <option value={trainer.id} key={trainer.id}>
                    {trainer.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="submit" className="primary">
            Create
          </button>
        </form>

        <form className="form-card" onSubmit={handleAssign}>
          <h3>Assign Trainee</h3>
          <label>
            Trainee
            <select
              value={assignPair.traineeId}
              onChange={(event) => setAssignPair((prev) => ({ ...prev, traineeId: event.target.value }))}
            >
              <option value="">Select</option>
              {trainees.map((trainee) => (
                <option key={trainee.id} value={trainee.id}>
                  {trainee.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Trainer
            <select
              value={assignPair.trainerId}
              onChange={(event) => setAssignPair((prev) => ({ ...prev, trainerId: event.target.value }))}
            >
              <option value="">Select</option>
              {trainers.map((trainer) => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer.name}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="secondary">
            Assign
          </button>
        </form>
        <form className="form-card" onSubmit={handlePlacementSubmit}>
          <h3>{editPlacementId ? "Edit Placement" : "Create Placement"}</h3>
          <label>
            Company
            <input
              name="company"
              value={placementForm.company}
              onChange={handlePlacementChange}
              required
            />
          </label>
          <label>
            Role
            <input
              name="role"
              value={placementForm.role}
              onChange={handlePlacementChange}
              required
            />
          </label>
          <label>
            Location
            <input
              name="location"
              value={placementForm.location}
              onChange={handlePlacementChange}
            />
          </label>
          <label>
            Package
            <input
              name="package"
              value={placementForm.package}
              onChange={handlePlacementChange}
            />
          </label>
          <label>
            Description
            <textarea
              name="description"
              rows="2"
              value={placementForm.description}
              onChange={handlePlacementChange}
            />
          </label>
          <button type="submit" className="primary">
            {editPlacementId ? "Update Placement" : "Publish Placement"}
          </button>
          {editPlacementId && (
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setEditPlacementId(null);
                setPlacementForm({ company: "", role: "", location: "", package: "", description: "" });
              }}
            >
              Cancel edit
            </button>
          )}
        </form>
      </div>

      <div className="card">
        <h3>Manage Placements</h3>
        {placements.length === 0 && <p className="muted">No placements yet.</p>}
        <table className="data-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Role</th>
              <th>Location</th>
              <th>Package</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {placements.map((p) => (
              <tr key={p.id}>
                <td>{p.company}</td>
                <td>{p.role}</td>
                <td>{p.location}</td>
                <td>{p.package}</td>
                <td>
                  <button className="ghost" onClick={() => startEditPlacement(p)}>
                    Edit
                  </button>
                  <button className="primary" onClick={() => onDeletePlacement(p.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Manage Users</h3>
        {users.length === 0 && <p className="muted">No users yet.</p>}
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Trainer</th>
              <th>Photo</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.role}</td>
                <td>{u.assignedTrainer || "-"}</td>
                <td>
                  {u.profilePhoto ? (
                    <img src={u.profilePhoto} alt={u.name} className="user-thumb" />
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  <button className="ghost" onClick={() => startEditUser(u)}>
                    Edit
                  </button>
                  <button className="primary" onClick={() => onDeleteUser(u.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editUser && (
        <div className="card">
          <h3>Edit User</h3>
          <form className="form-card" onSubmit={handleUserUpdate}>
            <label>
              Full name
              <input
                name="name"
                value={editUser.name}
                onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                required
              />
            </label>
            <label>
              Email
              <input
                name="email"
                value={editUser.email}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                required
              />
            </label>
            <label>
              Phone
              <input
                name="phone"
                value={editUser.phone}
                onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
              />
            </label>
            <label>
              Role
              <select
                name="role"
                value={editUser.role}
                onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
              >
                <option value="Admin">Admin</option>
                <option value="Trainer">Trainer</option>
                <option value="Trainee">Trainee</option>
              </select>
            </label>
            {editUser.role === "Trainee" && (
              <label>
                Assigned Trainer
                <select
                  name="assignedTrainer"
                  value={editUser.assignedTrainer}
                  onChange={(e) => setEditUser({ ...editUser, assignedTrainer: e.target.value })}
                >
                  <option value="">Select</option>
                  {trainers.map((trainer) => (
                    <option key={trainer.id} value={trainer.id}>
                      {trainer.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Profile photo URL
              <input
                name="profilePhoto"
                value={editUser.profilePhoto}
                onChange={(e) => setEditUser({ ...editUser, profilePhoto: e.target.value })}
                placeholder="https://..."
              />
            </label>
            <label>
              Or upload photo
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setEditUserFile(e.target.files?.[0] || null)}
              />
            </label>
            <label>
              Password (leave blank to keep current)
              <input
                name="password"
                type="password"
                value={editUser.password}
                onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
              />
            </label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button type="submit" className="primary">
                Save User
              </button>
              <button type="button" className="ghost" onClick={() => setEditUser(null)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <AttendanceTracker records={attendance} title="All Attendance Records" />
    </section>
  );
};

export default AdminDashboard;
